// src/components/ChatBox.jsx
import React, { useState, useEffect, useRef } from 'react';
import { ChatTranscript, ChatComposer } from './ChatUI';
import { io } from 'socket.io-client';
import api from '../api';

const API_URL = process.env.REACT_APP_API_URL || '/';

export default function ChatBox({ onClose }) {
  const [messages, setMessages] = useState([]);
  const [message, setMessage] = useState('');
  const [file, setFile] = useState(null);
  const [roomId, setRoomId] = useState(null);
  const [isTyping, setIsTyping] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const socketRef = useRef(null);
  const followRef = useRef(false);
  const [busy, setBusy] = useState(false);
  const [connected, setConnected] = useState(false);
  const [supportRequested, setSupportRequested] = useState(false);
  const token = localStorage.getItem('authToken');

  useEffect(() => {
    let active = true;
    const startChat = async () => {
      if (!token) return;

      try {
        const res = await api.post('/api/chat/start', {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (!active) return;
        setRoomId(res.data.room_id);

        socketRef.current = io(API_URL, {
          auth: { token },
          transports: ['polling', 'websocket'],
          path: '/socket.io'
        });

        const socket = socketRef.current;


        socket.on('previous_messages', (msgs) => {
          setMessages(msgs.map(m => ({ ...m, animate: false })));
          const lastHuman = Math.max(-1, ...msgs.map((m, index) => Number(m.is_admin) === 1 && Number(m.is_system) !== 1 ? index : -1));
          setSupportRequested(msgs.slice(lastHuman + 1).some(m => Number(m.is_admin) === 0 && m.message_text === 'درخواست پاسخ پشتیبانی: لطفاً یک پشتیبان به گفتگوی من رسیدگی کند.'));
        });

        socket.on('receive_message', (msg) => {
          if (Number(msg.is_admin) === 1 && Number(msg.is_system) !== 1) setSupportRequested(false);
          setMessages(prev => {
            const exists = prev.some(m => Number(m.id) === Number(msg.id));
            if (exists) return prev;
            return [...prev, { ...msg, animate: true }];
          });
          setIsTyping(false);
        });

        socket.on('typing', () => setIsTyping(true));
        socket.on('chat_error', ({ message }) => { setError(message); setSupportRequested(false); });
        socket.on('messages_read', ({ room_id, message_ids }) => {
          if (Number(room_id) !== Number(res.data.room_id)) return;
          setMessages(prev => prev.map(msg => message_ids.map(Number).includes(Number(msg.id)) ? { ...msg, is_read: 1, legacy_read_unknown: 0 } : msg));
        });
        socket.on('stop_typing', () => setIsTyping(false));

        socket.on('connect', () => { socket.emit('join_room', { room_id: res.data.room_id }); setConnected(true); setError(''); });
        socket.on('disconnect', () => { setConnected(false); setSupportRequested(false); });
        socket.on('connect_error', () => {
          if (!active) return;
          setError('اتصال به چت قطع شد');
          setTimeout(() => setError(''), 3000);
        });

      } catch (err) {
        if (!active) return;
        setError('خطا در شروع چت');
        setTimeout(() => setError(''), 3000);
        onClose?.();
      }
    };

    startChat();

    return () => {
      active = false;
      socketRef.current?.removeAllListeners();
      socketRef.current?.disconnect();
    };
  }, [token, onClose]);

  useEffect(() => {
    if (!message.trim() || !roomId || !socketRef.current) return;
  
    socketRef.current.emit('typing', { room_id: roomId });
    const timeout = setTimeout(() => {
      socketRef.current?.emit('stop_typing', { room_id: roomId });
    }, 1000);
  
    return () => clearTimeout(timeout);
  }, [message, roomId]);
  
  const sendMessage = async () => {
    if ((!message.trim() && !file) || busy || !socketRef.current?.connected) return;
    const socket = socketRef.current;
    setBusy(true);
    try {

      let file_url = null;
      let file_type = null;

      if (file) {
        const formData = new FormData();
        formData.append('file', file);

        try {
          const res = await api.post('/api/chat/upload', formData, {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'multipart/form-data',
            },
          });
          file_url = res.data.file_url;
          file_type = res.data.file_type;
          setSuccess('فایل آپلود شد');
          setTimeout(() => setSuccess(''), 2000);
        } catch (err) {
          setError('خطا در آپلود فایل');
          setTimeout(() => setError(''), 3000);
          return;
        }
      }

      if (!socket.connected || socketRef.current !== socket) return;
      socket.emit('send_message', {
        room_id: roomId,
        message: message.trim(),
        file_url,
        file_type,
      });

      setMessage('');
      setFile(null);
      followRef.current = true;
    } finally { setBusy(false); }
  };

  const requestSupport = () => {
    if (supportRequested || busy || !roomId || !socketRef.current?.connected) return;
    socketRef.current.emit('send_message', { room_id: roomId, message: 'درخواست پاسخ پشتیبانی: لطفاً یک پشتیبان به گفتگوی من رسیدگی کند.' });
    setSupportRequested(true);
    followRef.current = true;
  };
  return (
    <div className="chat-body">
      {error && <p className="chat-notice" role="alert">{error}</p>}
      {success && <p className="chat-notice chat-notice-success" role="status">{success}</p>}
      <ChatTranscript messages={messages} isTyping={isTyping} followRef={followRef} onRequestSupport={connected ? requestSupport : undefined} supportRequested={supportRequested} onSeen={ids => { if (socketRef.current?.connected) socketRef.current.emit('read_messages', { room_id: roomId, message_ids: ids }); }} />
      <ChatComposer message={message} setMessage={setMessage} file={file} setFile={setFile} onSend={sendMessage} busy={busy} ready={connected && Boolean(roomId)} />
    </div>
  );
}
